<template>
    <b-card
        class="list-container shadow-sm"
        bg-variant="white"
        no-body
    >
        <template #header>
            <div class="d-flex align-items-center justify-content-between" style="height: 36px">
                <div>
                    <h6 class="mb-0">사용자 목록</h6>
                </div>
                <div class="d-flex align-items-center">
                    <template v-if="!readOnly">
                        <b-button size="sm"
                                  class="mr-2"
                                  variant="dark-border"
                                  @click="openUserSearchModal"
                        >
                            {{ $t('기존 사용자 추가')}}
                        </b-button>
                        <b-button size="sm"
                                  variant="dark-border"
                                  @click="$bvModal.show('user-create-modal')"
                        >
                            {{ $t('새 사용자 생성')}}
                        </b-button>
                    </template>
                </div>
            </div>
        </template>
        <!-- DS 표 규칙: 행 44 고정 — 긴 이름 · 이메일은 줄이고 전체는 title. 폭은 머리가 정한다 -->
        <b-table
            responsive
            :items="members"
            :fields="tableColumns"
            class="mb-0 sticky-header-sort-fix custom-table"
            table-class="ds-table ds-table-fixed company-member-table"
            :empty-html="getEmptyTableHTML"
            :sticky-header="true"
            show-empty
        >
            <!-- 번호는 표시 순서(정렬하면 다시 1부터) — 식별자가 아니다 -->
            <template #cell(no)="row">
                {{ row.index + 1 }}
            </template>
            <template #cell(user_name)="row">
                <span class="ds-ellipsis" :title="row.value">{{ row.value }}</span>
            </template>
            <template #cell(e_mail)="row">
                <span class="ds-ellipsis" :title="row.value">{{ row.value }}</span>
            </template>
            <template #cell(is_admin)="row">
                <span class="ds-tag">{{ userLv(row.item.is_admin) }}</span>
            </template>
        </b-table>
        <UserCreateModal
            :company-id="company.company_id"
            @created="onUserCreated"
        />
        <UserSearchModal
            :exclude-company-id="company.company_id"
            @on-select="onSelectUser"
        />
        <AssessmentJoinModal
            ref="assessmentJoinModal"
            :company-id="company.company_id"
            :projects="company.projects || []"
            @completed="onAssessmentJoinCompleted"
        />
    </b-card>
</template>

<script>
import {getEmptyTableHTML} from '@shared/components/admin/pages/Management/common-data';
import {getUserLevelLabel} from '@shared/components/admin/pages/Management/Company/data';
import {getErrorMessage} from '@core/utils/api/errorMessage';
import UserCreateModal from './UserCreateModal.vue';
import UserSearchModal from './UserSearchModal.vue';
import AssessmentJoinModal from './AssessmentJoinModal.vue';

// DS 표 규칙: No. → 메인(사용자명) → 데이터. 폭 없는 칸(사용자명 · 이메일)이 남는 폭을 나눠 갖는다
const columns = [
    {key: 'no', label: 'No.', thClass: 'ds-cell-no', tdClass: 'ds-cell-no'},
    {key: 'user_name', label: '사용자명', sortable: true},
    {key: 'e_mail', label: '이메일(로그인 ID)', sortable: true},
    {key: 'user_id', label: '유저 ID', sortable: true, thClass: 'text-right', tdClass: 'ds-cell-num', thStyle: {width: '100px'}},
    {key: 'is_admin', label: '사용자 레벨', sortable: true, thStyle: {width: '160px'}},
];

export default {
    name: 'CompanyMembers',
    components: { UserCreateModal, UserSearchModal, AssessmentJoinModal },
    props: {
        company: {
            type: Object,
            default: () => ({id: null}),    
        },
        members: {
            type: Array,
            required: true,
            default: () => [],
        },
        readOnly: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            editLoading: false,
            detailUser: null,
        };
    },
    computed: {
        userLv() {
            return (is_admin) => getUserLevelLabel(is_admin);
        },
        tableColumns() {
            return columns.map(column => ({...column, label: this.$t(column.label)}));
        },
        getEmptyTableHTML() {
            return getEmptyTableHTML(this.$t('조회된 사용자가 없습니다.'), '521px');
        },
    },
    methods: {
        openUserSearchModal(){
            this.$bvModal.show('user-search-modal');    
            
        },
        async onSelectUser(users){
            const h = this.$createElement;
            const target = this.company.company_name || '이 기업';
            this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: this.$t('사용자 추가'),
                message: h('div', [
                    h('p', {class: 'mb-2'}, `선택한 ${Object.keys(users).length}명의 소속을 [${target}] 으로 이동합니다. 원래 소속에서는 빠집니다.`),
                    h('ul', {class: 'mb-0 pl-3'}, Object.values(users).map(user =>
                        h('li', `${user.user_name} (${user.e_mail}) : ${user.company_name || '소속 없음'} → ${target}`)
                    )),
                ]),
            })
                .then(value => {
                    if(value){
                        this.updateUserCompany(users);
                    }
                });
        },
        async updateUserCompany(users){
            /**
             * @todo array로 사용자 등록하는 api로 교체 필요
             * 추가로 api에서 company_id를 변경할 수 있도록 대응 필요.
             */
            this.editLoading = true;
            try {
                const entries = Object.entries(users);
                const results = await Promise.allSettled(entries.map(async ([key, value]) => {
                    // 사용자 검색 응답에는 is_admin 이 없다. 0 으로 보내면 레벨이 강등되므로 현재 레벨을 조회해 그대로 유지한다
                    const lookup = await this.$axios.get('/users/admin', {params: {e_mail: value.e_mail}});
                    const current = (lookup.data || []).find(user => String(user.id) === String(key));
                    if (!current || current.is_admin == null) {
                        throw new Error('현재 사용자 레벨을 확인하지 못했습니다.');
                    }
                    return this.$axios.post('/admin/company/member', {
                        user_id: Number(key),
                        is_update: true,
                        is_password_change: false,
                        company_id: Number(this.company.company_id),
                        user_name: value.user_name,
                        is_admin: current.is_admin
                    });
                }));

                const transferredUsers = [];
                const failures = [];
                results.forEach((result, idx) => {
                    const user = entries[idx][1];
                    if (result.status === 'fulfilled') {
                        this.$emit('updateMember', result.value.data);
                        transferredUsers.push({
                            user_id: result.value.data.id || result.value.data.user_id,
                            user_name: result.value.data.user_name,
                        });
                    } else {
                        console.error(result.reason);
                        failures.push(`${user.user_name}: ${getErrorMessage(result.reason, result.reason.message || '요청 실패')}`);
                    }
                });

                if (failures.length > 0) {
                    this.$EventBus.$emit(transferredUsers.length > 0 ? 'toast-warning' : 'toast-failure', this.$t('사용자 추가'),
                        `${transferredUsers.length}명 추가, ${failures.length}명 실패했습니다. ${failures.join(' / ')}`);
                }
                if (transferredUsers.length === 0) {
                    return;
                }

                this.$bvModal.hide('user-search-modal');
                this.$showModal({
                    type: 'alert',
                    state: 'success',
                    title: this.$t('사용자 추가 완료'),
                    message: this.$t('선택한 사용자를 이 기업에 추가했습니다.'),
                }).then(() => {
                    this.$refs.assessmentJoinModal.show(transferredUsers);
                });
            }
            finally {
                this.editLoading = false;
            }
        },
        onUserCreated(createdUser) {
            this.$emit('updateMember', createdUser);
        },
        onAssessmentJoinCompleted() {
            // 필요 시 상위 컴포넌트에 갱신 요청
        }
    }
};
</script>

<style lang="scss" scoped>
/* 고정 열 합 324(No. 64 · 유저 ID 100 · 레벨 160) + 사용자명 · 이메일 ~198씩. 기업 상세 탭(1366 메뉴 펼침 표 폭 약 1038)과
   CompanyModal(xl — 992~1199 에서 표 폭 약 748) 안에 들어가게 — 그보다 좁으면 가로 스크롤 */
::v-deep .company-member-table {
    min-width: 720px;
}
</style>
